import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CheckIcon, ShieldCheckIcon, CalendarIcon, DollarSignIcon, PlusIcon, StarIcon, ArrowLeftIcon } from 'lucide-react';
import { ProgressBar } from './ProgressBar';
import { User, MichiganLicenseData, RenewalEntry, MICHIGAN_STEPS, createDefaultMichiganLicense } from './types';
interface MichiganModalProps {
  user: User;
  isOpen: boolean;
  onClose: () => void;
  license: MichiganLicenseData;
  onUpdateLicense: (license: MichiganLicenseData) => void;
}
export function MichiganModal({
  user,
  isOpen,
  onClose,
  license,
  onUpdateLicense
}: MichiganModalProps) {
  const completedCount = license.steps.filter(Boolean).length;
  const allComplete = completedCount === MICHIGAN_STEPS.length;
  const stepTotalCost = license.stepCosts.reduce((sum, cost) => sum + (cost || 0), 0);
  const renewalsTotalCost = license.renewals.reduce((sum, r) => sum + (r.fee || 0), 0);
  const totalCost = stepTotalCost + renewalsTotalCost;
  const [isAddingRenewal, setIsAddingRenewal] = useState(false);
  const [newRenewalYear, setNewRenewalYear] = useState<number>(new Date().getFullYear() + 1);
  const [newRenewalFee, setNewRenewalFee] = useState<number>(0);
  const [newRenewalDate, setNewRenewalDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const handleToggleStep = (index: number) => {
    const newSteps = [...license.steps];
    newSteps[index] = !newSteps[index];
    onUpdateLicense({
      ...license,
      steps: newSteps
    });
  };
  const handleStepCostChange = (index: number, cost: number) => {
    const newCosts = [...license.stepCosts];
    newCosts[index] = cost;
    onUpdateLicense({
      ...license,
      stepCosts: newCosts
    });
  };
  const handleStartDateChange = (date: string) => {
    onUpdateLicense({
      ...license,
      startDate: date
    });
  };
  const handleExpirationDateChange = (date: string) => {
    onUpdateLicense({
      ...license,
      expirationDate: date
    });
  };
  const handleSaveRenewal = () => {
    const newRenewals = [...license.renewals, {
      year: newRenewalYear,
      fee: newRenewalFee,
      date: newRenewalDate
    }].sort((a, b) => b.year - a.year);
    onUpdateLicense({
      ...license,
      renewals: newRenewals
    });
    setIsAddingRenewal(false);
    setNewRenewalYear(new Date().getFullYear() + 1);
    setNewRenewalFee(0);
    setNewRenewalDate(new Date().toISOString().split('T')[0]);
  };
  const handleRemoveRenewal = (index: number) => {
    const newRenewals = [...license.renewals];
    newRenewals.splice(index, 1);
    onUpdateLicense({
      ...license,
      renewals: newRenewals
    });
  };
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-79">
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6" data-id="element-80">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-81" />

        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="relative w-full max-w-2xl bg-slate-900 border border-amber-500/30 rounded-2xl shadow-[0_0_40px_rgba(245,158,11,0.1)] overflow-hidden flex flex-col max-h-[90vh]" data-id="element-82">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-900/50" data-id="element-83">
            <div className="flex items-center gap-3" data-id="element-84">
              <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors" data-id="element-85">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-86" />
              </button>
              <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center" data-id="element-87">
                <StarIcon className="w-4 h-4 text-amber-500 fill-amber-500" data-id="element-88" />
              </div>
              <div data-id="element-89">
                <h2 className="text-lg font-bold text-white" data-id="element-90">
                  Michigan License
                </h2>
                <p className="text-xs text-slate-500" data-id="element-91">
                  {user.name} · #{user.nmlsId}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4" data-id="element-92">
              <AnimatePresence mode="wait" data-id="element-93">
                {allComplete ? <motion.div key="licensed" initial={{
                opacity: 0,
                scale: 0.8
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.8
              }} className="flex items-center gap-1.5 px-3 py-1 bg-emerald-500/15 border border-emerald-500/25 rounded-full" data-id="element-94">
                    <ShieldCheckIcon className="w-3.5 h-3.5 text-emerald-400" data-id="element-95" />
                    <span className="text-xs font-semibold text-emerald-400" data-id="element-96">
                      Licensed
                    </span>
                  </motion.div> : <motion.div key="in-progress" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} className="flex items-center gap-1.5 px-3 py-1 bg-amber-500/10 border border-amber-500/20 rounded-full" data-id="element-97">
                    <span className="text-xs font-semibold text-amber-400" data-id="element-98">
                      In Progress
                    </span>
                  </motion.div>}
              </AnimatePresence>
              <button onClick={onClose} className="p-2 -mr-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-full transition-colors" data-id="element-99">
                <XIcon className="w-5 h-5" data-id="element-100" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto custom-scrollbar flex-1 space-y-8" data-id="element-101">
            {/* Progress & Cost */}
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4" data-id="element-102">
              <div className="flex-1 max-w-md" data-id="element-103">
                <ProgressBar current={completedCount} total={MICHIGAN_STEPS.length} colorClass="bg-amber-500" data-id="element-104" />
              </div>
              <div className="flex items-center gap-2 bg-slate-800/50 border border-slate-700/50 rounded-xl px-4 py-2" data-id="element-105">
                <span className="text-xs font-medium text-slate-400 uppercase tracking-wider" data-id="element-106">
                  Total Cost
                </span>
                <span className="text-lg font-bold text-white tabular-nums" data-id="element-107">
                  ${totalCost.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Start Date */}
            <div data-id="element-108">
              <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider mb-2" data-id="element-109">
                Sponsorship Start Date
              </label>
              <div className="flex items-center bg-slate-800/50 border border-slate-700 rounded-xl px-3 py-2.5 focus-within:border-amber-500/50 focus-within:ring-1 focus-within:ring-amber-500/50 transition-all max-w-xs" data-id="element-110">
                <CalendarIcon className="w-4 h-4 text-slate-500 mr-2" data-id="element-111" />
                <input type="date" value={license.startDate || ''} onChange={e => handleStartDateChange(e.target.value)} className="w-full bg-transparent text-sm text-slate-200 focus:outline-none" style={{
                colorScheme: 'dark'
              }} data-id="element-112" />
              </div>
            </div>

            {/* Checklist */}
            <div data-id="element-113">
              <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider mb-3" data-id="element-114">
                Licensing Steps
              </label>
              <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-2" data-id="element-115">
                <ol className="space-y-1" data-id="element-116">
                  {MICHIGAN_STEPS.map((stepLabel, index) => {
                  const isComplete = license.steps[index];
                  const cost = license.stepCosts[index];
                  return <li key={index} data-id="element-117">
                        <div className="w-full flex items-start gap-3.5 py-3 px-3 rounded-xl transition-colors duration-150 hover:bg-slate-800/60 group" data-id="element-118">
                          <button onClick={() => handleToggleStep(index)} className="relative flex-shrink-0 mt-0.5 focus:outline-none" aria-label={`${isComplete ? 'Uncheck' : 'Check'}: ${stepLabel}`} data-id="element-119">
                            <motion.div className={`w-7 h-7 rounded-lg flex items-center justify-center border-2 transition-colors duration-200 ${isComplete ? 'bg-emerald-500 border-emerald-500' : 'border-slate-600 group-hover:border-slate-500'}`} whileTap={{
                          scale: 0.9
                        }} data-id="element-120">
                              <AnimatePresence mode="wait" data-id="element-121">
                                {isComplete ? <motion.div key="check" initial={{
                              opacity: 0,
                              scale: 0
                            }} animate={{
                              opacity: 1,
                              scale: 1
                            }} exit={{
                              opacity: 0,
                              scale: 0
                            }} transition={{
                              type: 'spring',
                              stiffness: 500,
                              damping: 30
                            }} data-id="element-122">
                                    <CheckIcon className="w-4 h-4 text-white" strokeWidth={3} data-id="element-123" />
                                  </motion.div> : <motion.span key="number" initial={{
                              opacity: 0
                            }} animate={{
                              opacity: 1
                            }} exit={{
                              opacity: 0
                            }} className="text-[11px] font-bold text-slate-500" data-id="element-124">
                                    {index + 1}
                                  </motion.span>}
                              </AnimatePresence>
                            </motion.div>
                            {index < MICHIGAN_STEPS.length - 1 && <div className={`absolute left-1/2 -translate-x-1/2 top-full w-0.5 h-[calc(0.25rem+12px)] transition-colors duration-300 ${isComplete ? 'bg-emerald-500/40' : 'bg-slate-700/50'}`} data-id="element-125" />}
                          </button>

                          <button onClick={() => handleToggleStep(index)} className="flex-1 pt-0.5 text-left focus:outline-none" data-id="element-126">
                            <span className={`text-sm leading-snug transition-all duration-200 ${isComplete ? 'text-slate-500 line-through decoration-slate-600' : 'text-slate-200 group-hover:text-white'}`} data-id="element-127">
                              {stepLabel}
                            </span>
                          </button>

                          <div className="flex-shrink-0 flex items-center bg-slate-900/50 rounded-lg px-2.5 py-1.5 border border-slate-700/50 focus-within:border-amber-500/50 focus-within:bg-slate-900 transition-colors" data-id="element-128">
                            <DollarSignIcon className="w-3.5 h-3.5 text-slate-500 mr-1" data-id="element-129" />
                            <input type="number" min="0" value={cost === 0 ? '' : cost} placeholder="0" onChange={e => handleStepCostChange(index, parseInt(e.target.value) || 0)} className="w-14 bg-transparent text-sm text-slate-300 tabular-nums focus:outline-none placeholder:text-slate-600" aria-label={`Cost for ${stepLabel}`} data-id="element-130" />
                          </div>
                        </div>
                      </li>;
                })}
                </ol>
              </div>
            </div>

            {/* Expiration & Renewals */}
            <AnimatePresence data-id="element-131">
              {allComplete && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} className="pt-6 border-t border-slate-800 space-y-6" data-id="element-132">
                  <div data-id="element-133">
                    <label className="block text-xs font-medium text-emerald-400/80 uppercase tracking-wider mb-2" data-id="element-134">
                      License Expiration
                    </label>
                    <div className="flex items-center bg-emerald-500/[0.05] border border-emerald-500/20 rounded-xl px-3 py-2.5 focus-within:border-emerald-500/50 focus-within:ring-1 focus-within:ring-emerald-500/50 transition-all max-w-xs" data-id="element-135">
                      <CalendarIcon className="w-4 h-4 text-emerald-500/70 mr-2" data-id="element-136" />
                      <input type="date" value={license.expirationDate || ''} onChange={e => handleExpirationDateChange(e.target.value)} className="w-full bg-transparent text-sm text-slate-200 focus:outline-none" style={{
                    colorScheme: 'dark'
                  }} data-id="element-137" />
                    </div>
                  </div>

                  <div data-id="element-138">
                    <div className="flex items-center justify-between mb-4" data-id="element-139">
                      <label className="block text-xs font-medium text-blue-400/80 uppercase tracking-wider" data-id="element-140">
                        Renewals
                      </label>
                      {!isAddingRenewal && <button onClick={() => setIsAddingRenewal(true)} className="flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 transition-colors bg-blue-500/10 px-3 py-1.5 rounded-lg" data-id="element-141">
                          <PlusIcon className="w-3.5 h-3.5" data-id="element-142" />
                          Add Renewal
                        </button>}
                    </div>

                    {license.renewals.length > 0 && <div className="space-y-2 mb-4" data-id="element-143">
                        {license.renewals.map((renewal, idx) => <div key={idx} className="flex items-center justify-between bg-slate-800/40 rounded-xl px-4 py-3 border border-slate-700/50 group" data-id="element-144">
                            <div className="flex flex-col" data-id="element-145">
                              <span className="text-sm text-slate-200 font-medium" data-id="element-146">
                                {renewal.year} Renewal
                              </span>
                              <span className="text-xs text-slate-500" data-id="element-147">
                                Submitted: {renewal.date}
                              </span>
                            </div>
                            <div className="flex items-center gap-4" data-id="element-148">
                              <span className="text-sm text-slate-300 tabular-nums font-medium" data-id="element-149">
                                ${renewal.fee}
                              </span>
                              <button onClick={() => handleRemoveRenewal(idx)} className="text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all p-1" aria-label="Remove renewal" data-id="element-150">
                                <XIcon className="w-4 h-4" data-id="element-151" />
                              </button>
                            </div>
                          </div>)}
                      </div>}

                    <AnimatePresence data-id="element-152">
                      {isAddingRenewal && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} className="overflow-hidden" data-id="element-153">
                          <div className="bg-slate-800/60 rounded-xl border border-slate-700 p-4" data-id="element-154">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4" data-id="element-155">
                              <div data-id="element-156">
                                <label className="block text-xs text-slate-400 mb-1.5" data-id="element-157">
                                  Year
                                </label>
                                <input type="number" value={newRenewalYear} onChange={e => setNewRenewalYear(parseInt(e.target.value) || new Date().getFullYear())} className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500/50" data-id="element-158" />
                              </div>
                              <div data-id="element-159">
                                <label className="block text-xs text-slate-400 mb-1.5" data-id="element-160">
                                  Fee
                                </label>
                                <div className="flex items-center bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 focus-within:border-blue-500/50" data-id="element-161">
                                  <DollarSignIcon className="w-4 h-4 text-slate-500 mr-1.5" data-id="element-162" />
                                  <input type="number" min="0" value={newRenewalFee === 0 ? '' : newRenewalFee} placeholder="0" onChange={e => setNewRenewalFee(parseInt(e.target.value) || 0)} className="w-full bg-transparent text-sm text-slate-200 tabular-nums focus:outline-none" data-id="element-163" />
                                </div>
                              </div>
                              <div data-id="element-164">
                                <label className="block text-xs text-slate-400 mb-1.5" data-id="element-165">
                                  Date Submitted
                                </label>
                                <input type="date" value={newRenewalDate} onChange={e => setNewRenewalDate(e.target.value)} className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500/50" style={{
                            colorScheme: 'dark'
                          }} data-id="element-166" />
                              </div>
                            </div>
                            <div className="flex items-center justify-end gap-3" data-id="element-167">
                              <button onClick={() => setIsAddingRenewal(false)} className="text-sm text-slate-400 hover:text-slate-300 font-medium px-3 py-1.5" data-id="element-168">
                                Cancel
                              </button>
                              <button onClick={handleSaveRenewal} className="text-sm bg-blue-500 text-white px-4 py-1.5 rounded-lg hover:bg-blue-600 transition-colors font-medium shadow-sm" data-id="element-169">
                                Save Renewal
                              </button>
                            </div>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-800 bg-slate-900/50 flex justify-end" data-id="element-170">
            <button onClick={onClose} className="px-6 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium rounded-xl transition-colors" data-id="element-171">
              Done
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}